<template>
  <div id="app">
    <div id="mountpoint"></div>

    <header class="header">
      <nav class="navbar is-link" role="navigation" aria-label="main navigation">
        <div class="container">
          <div class="navbar-brand">
            <router-link :to="parameterisedRoute('/')" class="navbar-item">
              <img src="./assets/img/logo-aiaibot.svg" width="150" height="36">
            </router-link>
          </div>

          <div id="navbar" class="navbar-menu">
            <div class="navbar-start">
              <router-link :to="parameterisedRoute('/home')" class="navbar-item"
                           active-class="is-active">
                Home
              </router-link>

              <router-link :to="parameterisedRoute('/login')" class="navbar-item"
                           active-class="is-active">
                Login
              </router-link>

              <div class="navbar-item has-dropdown is-hoverable">
                <router-link :to="parameterisedRoute('/features')" class="navbar-link"
                             active-class="is-active">
                  Features
                </router-link>

                <div class="navbar-dropdown">
                  <router-link :to="parameterisedRoute('/features/story-builder')"
                               class="navbar-item"
                               active-class="is-active">
                    Story Builder
                  </router-link>
                  <router-link :to="parameterisedRoute('/features/collaboration')"
                               class="navbar-item"
                               active-class="is-active">
                    Collaboration
                  </router-link>
                  <router-link :to="parameterisedRoute('/features/query-param-to-event')"
                               class="navbar-item"
                               active-class="is-active">
                    Custom Events via Query-Params
                  </router-link>

                  <hr class="navbar-divider" />

                  <div class="dropdown-item">
                    <p class="has-text-weight-bold">Theming</p>
                  </div>

                  <router-link :to="parameterisedRoute('/features/theming/colors')"
                               class="navbar-item"
                               active-class="is-active">
                    Colors
                  </router-link>

                  <router-link :to="parameterisedRoute('/features/theming/logos')"
                               class="navbar-item"
                               active-class="is-active">
                    Logos
                  </router-link>
                </div>
              </div>

              <div class="navbar-item has-dropdown is-hoverable">
                <router-link :to="parameterisedRoute('/about')" class="navbar-link"
                             active-class="is-active">
                  About
                </router-link>

                <div class="navbar-dropdown">
                  <router-link :to="parameterisedRoute('/about/contact')"
                               class="navbar-item"
                               active-class="is-active">
                    Contract
                  </router-link>
                  <!-- eslint-disable-next-line max-len -->
                  <router-link :to="parameterisedRoute('/about/company/our-history-that-spans-more-than-100-years-were-we-achieved-more-than-anyone-else')"
                               class="navbar-item"
                               active-class="is-active">
                    Company
                  </router-link>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div class="navbar-end">
          <div class="navbar-item has-text-white is-size-7">
            <span class="integration-info has-text-weight-bold">Host:</span>
            <span class="is-family-monospace" v-text="scriptHost || 'Not Set'"/>
          </div>
          <div class="navbar-item has-text-white is-size-7">
            <span class="integration-info has-text-weight-bold">API Key:</span>
            <span class="is-family-monospace" v-text="apiKey || 'Not Set'"/>
          </div>
          <div class="navbar-item">
            <div class="field has-addons">
              <div class="control">
                <input v-model="integrationCode" class="input integration-input" type="text"
                       placeholder="Enter Chatbot Integration Code"
                       @keyup.enter="onSaveIntegrationScript">
              </div>
              <div class="control">
                <button class="button is-dark" @click="onSaveIntegrationScript">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <section class="main">
      <div class="container content">
        <router-view/>
      </div>
    </section>

    <footer class="footer">
      <div class="footer--content is-flex">
        <div class="footer--instance-switcher">
          <span>Switch Instance: </span>
          <a :href="primaryInstanceLink" target="_blank">Primary</a> |
          <a href="#" target="_blank">Backup</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
const BACKUP_INSTANCE_HOSTNAME_SUFFIX = '-backup';

export default {
  data() {
    return {
      apiKey: '',
      scriptHost: '',
      integrationCode: '',
      scriptInjected: false,
    };
  },
  computed: {
    primaryInstanceLink() {
      const { fullPath } = this.$route;
      const { hostname, protocol, port } = window.location;

      let targetHostname = hostname;

      if (targetHostname.indexOf(BACKUP_INSTANCE_HOSTNAME_SUFFIX) > -1) {
        targetHostname = targetHostname.replace(BACKUP_INSTANCE_HOSTNAME_SUFFIX, '');
      }

      return `${protocol}//${targetHostname}:${port}${fullPath}`;
    },
    backupInstanceLink() {
      const { fullPath } = this.$route;
      const { hostname, protocol, port } = window.location;

      let targetHostname = hostname;

      if (targetHostname.indexOf(BACKUP_INSTANCE_HOSTNAME_SUFFIX) === -1) {
        targetHostname = `${targetHostname}${BACKUP_INSTANCE_HOSTNAME_SUFFIX}`;
      }

      return `${protocol}//${targetHostname}:${port}${fullPath}`;
    },
  },
  watch: {
    $route(to) {
      this.configureFromUrlParameters(to.query);
    },
  },
  mounted() {
    this.configureFromUrlParameters(this.$route.query);
  },
  methods: {
    parameterisedRoute(path) {
      return {
        path,
        query: {
          apiKey: encodeURIComponent(this.apiKey),
          scriptHost: encodeURIComponent(this.scriptHost),
        },
      };
    },
    configureFromUrlParameters(query) {
      this.apiKey = decodeURIComponent(query.apiKey || '');
      this.scriptHost = decodeURIComponent(query.scriptHost || '');

      if (this.apiKey !== '' && this.scriptHost !== '' && !this.scriptInjected) {
        this.injectIntegrationScript();
      }
    },
    onSaveIntegrationScript() {
      if (!this.integrationCode) {
        // eslint-disable-next-line no-alert
        alert('Please enter the Integration Code first!');
        return;
      }

      this.parseIntegrationScript(this.integrationCode);
      this.configureIntegration();
    },
    parseIntegrationScript(script) {
      // eslint-disable-next-line no-useless-escape
      const matches = script.match(/<script.*src="(http|https):\/\/([a-zA-Z0-9\-\.\:]+).*".*data-aiaibot-key="([a-zA-Z0-9\-]+)"/);
      const protocol = matches[1];
      const hostname = matches[2];
      const key = matches[3];

      this.scriptHost = `${protocol}://${hostname}`;
      this.apiKey = key;
    },
    configureIntegration() {
      const { path } = this.$route;
      this.$router.push(this.parameterisedRoute(path));
    },
    injectIntegrationScript() {
      const mountpoint = document.querySelector('#mountpoint');

      // First, remove all children to get started fresh
      mountpoint.innerHTML = '';

      // Insert the integration script, but without the defer attribute
      const scriptNode = document.createElement('script');

      scriptNode.setAttribute('type', 'text/javascript');
      scriptNode.setAttribute('src', `${this.scriptHost}/bootstrap.js`);
      scriptNode.setAttribute('data-aiaibot-key', this.apiKey);

      mountpoint.insertAdjacentElement('beforeend', scriptNode);

      this.scriptInjected = true;
    },
  },
  metaInfo: {
    meta: [
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  },
};
</script>

<style>
  #app {
    display: flex;
    min-height: 100vh;
    flex-direction: column;
  }

  .main {
    flex: 1;
  }

  .content {
    padding-top: 1em;
    padding-left: 1em;
  }

  .integration-info {
    margin-right: 0.5em;
  }

  .footer {
    position: sticky;
    bottom: 0;
    padding: 1em !important;
    font-size: 0.75em;
  }

  .footer--content {
    justify-content: center;
    align-items: center;
  }
</style>
